import { useState, useEffect, useRef } from 'react';
import { Download, Truck, MapPin, Loader2, FileText, DollarSign, X, Search, CheckCircle2, AlertTriangle, HelpCircle, Upload, ChevronDown, ChevronRight, Trash2, Pencil, Fuel } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { IFTARatesView } from './IFTARatesView';
import { IFTAReturnView } from './IFTAReturnView';
import { OwnerFuelView } from './OwnerFuelView';
import {
  parseProLogsMileageCSV,
  buildImportPreview,
  importProLogsCSV,
  mergeParsedFiles,
  renameIftaImport,
  deleteIftaImport,
  deleteIftaLegacyRows,
  type ParsedTripRow,
  type ImportPreview,
} from '../../services/prologsIftaCsvService';

type TabId = 'mileage' | 'return' | 'rates' | 'owner_fuel';

const LEGACY_KEY = '__legacy__';

interface ImportGroup {
  // null for pre-CSV/legacy rows (import_id is null in the DB)
  importId: string | null;
  label: string;
  uploadedAt: string | null;
  startDate: string;
  endDate: string;
  totalMiles: number;
  rowCount: number;
  truckCount: number;
  quarters: number[];
  states: Array<{ state: string; distance: number }>;
}

function quartersCovered(minDate: string, maxDate: string): number[] {
  const qs = new Set<number>();
  const a = new Date(`${minDate}T00:00:00Z`);
  const b = new Date(`${maxDate}T00:00:00Z`);
  const cursor = new Date(Date.UTC(a.getUTCFullYear(), a.getUTCMonth(), 1));
  const end = new Date(Date.UTC(b.getUTCFullYear(), b.getUTCMonth(), 1));
  while (cursor.getTime() <= end.getTime()) {
    qs.add(Math.ceil((cursor.getUTCMonth() + 1) / 3));
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return Array.from(qs).sort((x, y) => x - y);
}

export function IFTAView() {
  const [activeTab, setActiveTab] = useState<TabId>('mileage');
  const [importGroups, setImportGroups] = useState<ImportGroup[]>([]);
  const [collapsedImports, setCollapsedImports] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [trucks, setTrucks] = useState<Array<{ id: string; unit_number: string }>>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<string>('');
  const [vehicleSearch, setVehicleSearch] = useState<string>('');
  const [showVehicleDropdown, setShowVehicleDropdown] = useState<boolean>(false);
  const vehicleDropdownRef = useRef<HTMLDivElement | null>(null);
  const [presetYear, setPresetYear] = useState<number>(() => {
    const saved = localStorage.getItem('ifta_preset_year');
    const n = saved ? parseInt(saved, 10) : NaN;
    return Number.isFinite(n) ? n : new Date().getFullYear();
  });
  const [showHelp, setShowHelp] = useState<boolean>(false);
  const [guideCollapsed, setGuideCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('ifta_guide_collapsed') === '1';
    } catch {
      return false;
    }
  });
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importParsed, setImportParsed] = useState<ParsedTripRow[] | null>(null);
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null);
  const [importing, setImporting] = useState<boolean>(false);
  const [importFileNames, setImportFileNames] = useState<string[]>([]);
  const [importDuplicateRows, setImportDuplicateRows] = useState<number>(0);
  const [importLabel, setImportLabel] = useState<string>('');

  const toast = useToast();

  useEffect(() => {
    loadTrucks();
  }, []);

  // Auto-load imports whenever year or selected vehicle changes.
  useEffect(() => {
    localStorage.setItem('ifta_preset_year', String(presetYear));
    loadYearImports().catch(err => {
      console.error('Error loading IFTA imports:', err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetYear, selectedVehicle]);

  // Close vehicle dropdown on outside click
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!vehicleDropdownRef.current) return;
      if (!vehicleDropdownRef.current.contains(e.target as Node)) setShowVehicleDropdown(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const loadTrucks = async () => {
    try {
      const { data, error } = await supabase
        .from('trucks')
        .select('id, unit_number')
        .order('unit_number');
      if (error) throw error;
      setTrucks(data || []);
    } catch (error) {
      console.error('Error loading trucks:', error);
    }
  };

  // Query every row in the selected year, group by import_id. PostgREST caps
  // a single SELECT at 1000 rows, so paginate via .range() until exhausted.
  const loadYearImports = async (): Promise<void> => {
    setLoading(true);
    try {
      let truckId: string | undefined;
      if (selectedVehicle) {
        const truck = trucks.find(t => t.unit_number === selectedVehicle);
        if (truck) {
          truckId = truck.id;
        } else {
          setImportGroups([]);
          return;
        }
      }

      type Row = {
        truck_id: string;
        jurisdiction_code: string;
        miles: number | string;
        trip_date: string;
        import_id: string | null;
        import_label: string | null;
        import_uploaded_at: string | null;
      };
      const allRows: Row[] = [];
      const pageSize = 1000;
      let from = 0;
      while (true) {
        let q = supabase
          .from('ifta_state_miles')
          .select('truck_id, jurisdiction_code, miles, trip_date, import_id, import_label, import_uploaded_at')
          .gte('trip_date', `${presetYear}-01-01`)
          .lte('trip_date', `${presetYear}-12-31`)
          .order('trip_date', { ascending: true })
          .range(from, from + pageSize - 1);
        if (truckId) q = q.eq('truck_id', truckId);
        const { data, error } = await q;
        if (error) throw error;
        const page = (data || []) as Row[];
        allRows.push(...page);
        if (page.length < pageSize) break;
        from += pageSize;
      }

      type Agg = {
        importId: string | null;
        label: string;
        uploadedAt: string | null;
        total: number;
        rowCount: number;
        truckIds: Set<string>;
        states: Map<string, number>;
        minDate: string | null;
        maxDate: string | null;
      };
      const byImport = new Map<string, Agg>();

      for (const row of allRows) {
        const key = row.import_id || LEGACY_KEY;
        let agg = byImport.get(key);
        if (!agg) {
          agg = {
            importId: row.import_id,
            label: row.import_label || 'Pre-CSV data (legacy)',
            uploadedAt: row.import_uploaded_at,
            total: 0,
            rowCount: 0,
            truckIds: new Set(),
            states: new Map(),
            minDate: null,
            maxDate: null,
          };
          byImport.set(key, agg);
        }
        const miles = Number(row.miles) || 0;
        agg.total += miles;
        agg.rowCount++;
        agg.truckIds.add(row.truck_id);
        agg.states.set(row.jurisdiction_code, (agg.states.get(row.jurisdiction_code) || 0) + miles);
        if (!agg.minDate || row.trip_date < agg.minDate) agg.minDate = row.trip_date;
        if (!agg.maxDate || row.trip_date > agg.maxDate) agg.maxDate = row.trip_date;
        // Keep the newest upload timestamp if several rows carry one.
        if (row.import_uploaded_at && (!agg.uploadedAt || row.import_uploaded_at > agg.uploadedAt)) {
          agg.uploadedAt = row.import_uploaded_at;
        }
      }

      const groups: ImportGroup[] = Array.from(byImport.values()).map(agg => ({
        importId: agg.importId,
        label: agg.label,
        uploadedAt: agg.uploadedAt,
        startDate: agg.minDate || `${presetYear}-01-01`,
        endDate: agg.maxDate || `${presetYear}-12-31`,
        totalMiles: agg.total,
        rowCount: agg.rowCount,
        truckCount: agg.truckIds.size,
        quarters: quartersCovered(agg.minDate || `${presetYear}-01-01`, agg.maxDate || `${presetYear}-12-31`),
        states: Array.from(agg.states.entries())
          .filter(([, m]) => m > 0)
          .map(([state, distance]) => ({ state, distance }))
          .sort((a, b) => b.distance - a.distance),
      }));

      // Most recent upload first; legacy (null uploadedAt) sinks to the end.
      groups.sort((a, b) => {
        if (a.uploadedAt && b.uploadedAt) return b.uploadedAt.localeCompare(a.uploadedAt);
        if (a.uploadedAt) return -1;
        if (b.uploadedAt) return 1;
        return a.label.localeCompare(b.label);
      });

      setImportGroups(groups);
    } finally {
      setLoading(false);
    }
  };

  const toggleImport = (key: string) => {
    setCollapsedImports(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleRenameImport = async (group: ImportGroup) => {
    if (!group.importId) {
      toast.error('Legacy rows cannot be renamed — delete them instead.');
      return;
    }
    const next = window.prompt('Rename this import (e.g. company name):', group.label);
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === group.label) return;
    const result = await renameIftaImport(group.importId, trimmed);
    if (result.success) {
      toast.success(`Renamed to "${trimmed}"`);
      await loadYearImports();
    } else {
      toast.error(result.error || 'Rename failed');
    }
  };

  const handleDeleteImport = async (group: ImportGroup) => {
    const confirmed = window.confirm(
      `Delete import "${group.label}"?\n\n` +
      `This wipes ${group.rowCount.toLocaleString()} rows (${Math.round(group.totalMiles).toLocaleString()} mi across ${group.truckCount} truck${group.truckCount === 1 ? '' : 's'}).\n\n` +
      `This cannot be undone — re-import the CSV to restore.`
    );
    if (!confirmed) return;
    const result = group.importId
      ? await deleteIftaImport(group.importId)
      : await deleteIftaLegacyRows();
    if (result.success) {
      toast.success(`Deleted "${group.label}"`);
      await loadYearImports();
    } else {
      toast.error(result.error || 'Delete failed');
    }
  };

  const handleExportCSV = () => {
    if (importGroups.length === 0) {
      toast.error('No data to export');
      return;
    }
    try {
      const headers = ['Import', 'Uploaded', 'Date range', 'Jurisdiction', 'Distance (miles)'];
      const lines = [headers.join(',')];
      for (const g of importGroups) {
        for (const s of g.states) {
          lines.push(
            [
              JSON.stringify(g.label),
              g.uploadedAt || '',
              `${g.startDate} → ${g.endDate}`,
              s.state,
              s.distance.toFixed(2),
            ].join(',')
          );
        }
      }
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `ifta-${presetYear}${selectedVehicle ? `-${selectedVehicle}` : ''}.csv`
      );
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('IFTA report exported to CSV');
    } catch (error: any) {
      console.error('Error exporting CSV:', error);
      toast.error(error.message || 'Failed to export CSV');
    }
  };

  const handleImportFilePick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    // Reset the input so picking the same file twice re-fires change.
    e.target.value = '';
    if (files.length === 0) return;
    try {
      // Several files (e.g. one ProLogs export per truck) become one import.
      const perFile: ParsedTripRow[][] = [];
      const usedNames: string[] = [];
      const emptyNames: string[] = [];
      for (const file of files) {
        const rows = parseProLogsMileageCSV(await file.text());
        if (rows.length === 0) {
          emptyNames.push(file.name);
        } else {
          perFile.push(rows);
          usedNames.push(file.name);
        }
      }
      if (perFile.length === 0) {
        toast.error('No trip rows found. Is this the ProLogs Vehicle mileage report export?');
        return;
      }
      if (emptyNames.length > 0) {
        toast.error(`Skipped ${emptyNames.length} file${emptyNames.length === 1 ? '' : 's'} with no trip rows: ${emptyNames.join(', ')}`);
      }
      const { rows: parsed, duplicateRows } = mergeParsedFiles(perFile);
      const preview = await buildImportPreview(parsed);
      setImportParsed(parsed);
      setImportPreview(preview);
      setImportFileNames(usedNames);
      setImportDuplicateRows(duplicateRows);
      // Default the import label to the filename stem (or the date range when
      // several files are combined) — the user can rename it before confirming.
      if (usedNames.length === 1) {
        const stem = usedNames[0].replace(/\.(csv|tsv|txt)$/i, '').trim() || usedNames[0];
        setImportLabel(stem);
      } else {
        setImportLabel(preview.dateRange ? `ProLogs ${preview.dateRange.min} to ${preview.dateRange.max}` : 'ProLogs mileage');
      }
    } catch (err: any) {
      console.error('CSV parse error:', err);
      toast.error(err?.message || 'Failed to read CSV');
    }
  };

  const handleConfirmImport = async () => {
    if (!importParsed || !importPreview) return;
    const label = importLabel.trim();
    if (!label) {
      toast.error('Please give this import a name first');
      return;
    }
    setImporting(true);
    try {
      const result = await importProLogsCSV(importParsed, importPreview, label);
      if (result.success) {
        toast.success(
          `Imported "${label}" — ${result.insertedRows?.toLocaleString()} row${result.insertedRows === 1 ? '' : 's'} for ${result.truckCount} truck${result.truckCount === 1 ? '' : 's'}`
        );
        if (result.unmatched && result.unmatched.length > 0) {
          toast.info?.(`Imported ${result.unmatched.length} unmatched vehicle${result.unmatched.length === 1 ? '' : 's'} without a truck link: ${result.unmatched.join(', ')}`);
        }
        setImportParsed(null);
        setImportPreview(null);
        setImportFileNames([]);
        setImportDuplicateRows(0);
        setImportLabel('');

        // Switch the year selector to match the CSV if it's different, then
        // reload. Changing presetYear alone triggers the useEffect load; if the
        // year already matches, call load directly.
        if (importPreview.dateRange) {
          const csvYear = parseInt(importPreview.dateRange.min.slice(0, 4), 10);
          if (Number.isFinite(csvYear) && csvYear !== presetYear) {
            setPresetYear(csvYear);
          } else {
            await loadYearImports();
          }
        } else {
          await loadYearImports();
        }
      } else {
        toast.error(result.error || 'Import failed');
      }
    } finally {
      setImporting(false);
    }
  };

  const handleCancelImport = () => {
    setImportParsed(null);
    setImportPreview(null);
    setImportFileNames([]);
    setImportDuplicateRows(0);
    setImportLabel('');
  };

  const yearOptions = (() => {
    const current = new Date().getFullYear();
    const years: number[] = [];
    for (let y = current + 1; y >= current - 5; y--) years.push(y);
    return years;
  })();

  const tabs: Array<{ id: TabId; label: string; icon: typeof MapPin }> = [
    { id: 'mileage', label: 'Mileage', icon: MapPin },
    { id: 'return', label: 'Return', icon: FileText },
    { id: 'rates', label: 'Tax Rates', icon: DollarSign },
    { id: 'owner_fuel', label: 'Owner Fuel', icon: Fuel },
  ];

  const toggleGuide = () => {
    const next = !guideCollapsed;
    setGuideCollapsed(next);
    try {
      localStorage.setItem('ifta_guide_collapsed', next ? '1' : '0');
    } catch {
      // Storage blocked (private window) — the panel just won't remember.
    }
  };

  const guideSteps: Array<{ title: string; body: React.ReactNode; tab?: TabId }> = [
    {
      title: 'Load tax rates',
      body: <>Pick the quarter and click <em>Fetch rates</em>.</>,
      tab: 'rates',
    },
    {
      title: 'Import fuel',
      body: <>In <span className="font-medium">Accounting → Fuel</span>, upload the statement of every fuel card for the quarter. Fuel paid by owner-operators goes in <em>Owner Fuel</em>.</>,
      tab: 'owner_fuel',
    },
    {
      title: 'Import miles from ProLogs',
      body: (
        <>
          Miles do <span className="font-medium">not</span> arrive from the ELD automatically. In ProLogs, export the{' '}
          <em>Vehicle mileage report</em> for the quarter's dates (include as many trucks as ProLogs allows per file).
          Then click <em>Import CSV</em> and select <span className="font-medium">all the files at once</span>. Name it e.g. "2026-Q3".
        </>
      ),
      tab: 'mileage',
    },
    {
      title: 'Review and export the return',
      body: <>Pick the quarter. Total miles must not be 0 and Fleet MPG should land around 5–8. Then click <em>CSV</em>.</>,
      tab: 'return',
    },
  ];

  const filteredTrucks = trucks.filter(t =>
    t.unit_number.toLowerCase().includes(vehicleSearch.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 pt-5 pb-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div>
                <h1 className="text-xl font-semibold text-slate-900">IFTA</h1>
                <p className="text-sm text-slate-500">International Fuel Tax Agreement reporting</p>
              </div>
              <button
                onClick={() => setShowHelp(true)}
                title="How the IFTA workflow works"
                className="ml-2 inline-flex items-center justify-center w-7 h-7 rounded-full border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
              >
                <HelpCircle className="w-4 h-4" />
              </button>
            </div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium border border-slate-200 bg-slate-50 text-slate-600">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
              {presetYear}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 -mb-px">
            {tabs.map(t => {
              const Icon = t.icon;
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    active
                      ? 'border-blue-600 text-blue-700'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
        {/* Quarterly guide — always visible so users don't have to find the help icon */}
        <div className="bg-white border border-blue-200 rounded-xl overflow-hidden">
          <button
            onClick={toggleGuide}
            className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-blue-50/50"
          >
            {guideCollapsed ? <ChevronRight className="w-4 h-4 text-blue-600" /> : <ChevronDown className="w-4 h-4 text-blue-600" />}
            <span className="text-sm font-semibold text-slate-900">How to file a quarter</span>
            <span className="text-xs text-slate-500">4 steps after the quarter ends</span>
          </button>
          {!guideCollapsed && (
            <div className="px-4 pb-4">
              <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {guideSteps.map((step, idx) => (
                  <li key={step.title} className="flex flex-col p-3 rounded-lg border border-slate-200 bg-slate-50">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-[11px] font-bold">
                        {idx + 1}
                      </span>
                      <span className="text-sm font-semibold text-slate-900">{step.title}</span>
                    </div>
                    <p className="text-xs text-slate-600 flex-1">{step.body}</p>
                    {step.tab && step.tab !== activeTab && (
                      <button
                        onClick={() => setActiveTab(step.tab!)}
                        className="mt-2 self-start text-xs font-medium text-blue-700 hover:text-blue-800 hover:underline"
                      >
                        Go to {tabs.find(t => t.id === step.tab)?.label} →
                      </button>
                    )}
                  </li>
                ))}
              </ol>
              <button
                onClick={() => setShowHelp(true)}
                className="mt-3 text-xs font-medium text-slate-500 hover:text-slate-700 hover:underline"
              >
                Full guide, deadlines and pre-submit checklist
              </button>
            </div>
          )}
        </div>

        {activeTab === 'return' && <IFTAReturnView onGoToMileage={() => setActiveTab('mileage')} />}
        {activeTab === 'rates' && <IFTARatesView />}
        {activeTab === 'owner_fuel' && <OwnerFuelView year={presetYear} />}

        {activeTab === 'mileage' && (
          <>
            {/* Filter bar */}
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col">
                  <label className="text-[11px] font-medium text-slate-500 mb-1 uppercase tracking-wide">Year</label>
                  <select
                    value={presetYear}
                    onChange={(e) => setPresetYear(parseInt(e.target.value, 10))}
                    className="px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {yearOptions.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col min-w-[220px]" ref={vehicleDropdownRef}>
                  <label className="text-[11px] font-medium text-slate-500 mb-1 uppercase tracking-wide">
                    Vehicle <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    {selectedVehicle && !showVehicleDropdown ? (
                      <div className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg bg-white text-sm">
                        <Truck className="w-4 h-4 text-slate-400" />
                        <span className="font-medium text-slate-900">{selectedVehicle}</span>
                        <button
                          onClick={() => { setSelectedVehicle(''); setVehicleSearch(''); setShowVehicleDropdown(true); }}
                          className="ml-auto text-slate-400 hover:text-slate-600"
                          aria-label="Clear vehicle"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        <input
                          type="text"
                          value={vehicleSearch}
                          onChange={(e) => { setVehicleSearch(e.target.value); setShowVehicleDropdown(true); }}
                          onFocus={() => setShowVehicleDropdown(true)}
                          placeholder="Search truck unit..."
                          className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </>
                    )}

                    {showVehicleDropdown && (
                      <div className="absolute z-30 mt-1 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                        {filteredTrucks.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-slate-500">No matches</div>
                        ) : (
                          filteredTrucks.map(t => (
                            <button
                              key={t.id}
                              onClick={() => { setSelectedVehicle(t.unit_number); setVehicleSearch(''); setShowVehicleDropdown(false); }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-50 ${
                                selectedVehicle === t.unit_number ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700'
                              }`}
                            >
                              <Truck className="w-4 h-4 text-slate-400" />
                              {t.unit_number}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 ml-auto items-center">
                  {loading && (
                    <span className="flex items-center gap-1.5 text-xs text-slate-500">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Loading...
                    </span>
                  )}
                  <button
                    onClick={handleImportFilePick}
                    disabled={importing}
                    title="Upload one or more ProLogs Vehicle mileage report CSVs — select several files at once to load many trucks in one import"
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    Import CSV
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
                    multiple
                    onChange={handleImportFileChange}
                    className="hidden"
                  />

                  <button
                    onClick={handleExportCSV}
                    disabled={importGroups.length === 0}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Export CSV
                  </button>
                </div>
              </div>
            </div>

            {/* Per-import cards */}
            {importGroups.length > 0 ? (
              <div className="space-y-3">
                {importGroups.map(g => {
                  const key = g.importId || LEGACY_KEY;
                  const isCollapsed = collapsedImports.has(key);
                  const isLegacy = g.importId === null;
                  const qLabel = g.quarters.length === 0
                    ? ''
                    : g.quarters.length === 1
                    ? `Q${g.quarters[0]}`
                    : `Q${g.quarters[0]}-Q${g.quarters[g.quarters.length - 1]}`;
                  return (
                    <div key={key} className={`bg-white border rounded-xl overflow-hidden ${isLegacy ? 'border-amber-200' : 'border-slate-200'}`}>
                      <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-3">
                        <button
                          onClick={() => toggleImport(key)}
                          className="p-1 rounded-md hover:bg-slate-100 text-slate-500"
                          aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                        >
                          {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {qLabel && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold border ${isLegacy ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                                {qLabel} {new Date(g.startDate).getUTCFullYear()}
                              </span>
                            )}
                            <span className="text-sm font-semibold text-slate-900 truncate max-w-[340px]" title={g.label}>
                              {g.label}
                            </span>
                            {!isLegacy && (
                              <button
                                onClick={() => handleRenameImport(g)}
                                title="Rename this import"
                                className="p-1 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <span className="text-xs text-slate-500">
                              {selectedVehicle ? `· Truck ${selectedVehicle}` : ''}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {g.startDate} → {g.endDate} · {g.truckCount} truck{g.truckCount === 1 ? '' : 's'} · {g.states.length} jurisdiction{g.states.length === 1 ? '' : 's'}
                            {g.uploadedAt && (
                              <> · uploaded {new Date(g.uploadedAt).toLocaleString()}</>
                            )}
                            {isLegacy && (
                              <> · pre-CSV rows (no import tag)</>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-[11px] text-slate-500 uppercase tracking-wide">Total</div>
                          <div className="text-lg font-semibold text-slate-900 tabular-nums">
                            {g.totalMiles.toLocaleString(undefined, { maximumFractionDigits: 0 })} <span className="text-sm font-normal text-slate-500">mi</span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteImport(g)}
                          title={`Delete "${g.label}"`}
                          className="p-2 rounded-lg text-rose-500 hover:bg-rose-50 hover:text-rose-600 border border-transparent hover:border-rose-200 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {!isCollapsed && (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-slate-100 text-sm">
                            <thead className="bg-slate-50">
                              <tr>
                                <th className="px-5 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Jurisdiction</th>
                                <th className="px-5 py-2.5 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Miles</th>
                                <th className="px-5 py-2.5 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Share</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {g.states.map((r, idx) => {
                                const share = g.totalMiles > 0 ? (r.distance / g.totalMiles) * 100 : 0;
                                return (
                                  <tr key={`${r.state}-${idx}`} className="hover:bg-slate-50">
                                    <td className="px-5 py-2.5">
                                      <span className="inline-flex items-center justify-center w-8 h-5 rounded text-[10px] font-bold bg-slate-100 text-slate-600">
                                        {r.state}
                                      </span>
                                    </td>
                                    <td className="px-5 py-2.5 text-right tabular-nums font-medium text-slate-900">
                                      {r.distance.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-5 py-2.5 text-right text-slate-500 tabular-nums">
                                      {share.toFixed(1)}%
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot className="bg-slate-50 border-t border-slate-200">
                              <tr>
                                <td className="px-5 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">Total</td>
                                <td className="px-5 py-2.5 text-right tabular-nums font-semibold text-slate-900">
                                  {g.totalMiles.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                </td>
                                <td className="px-5 py-2.5 text-right text-slate-500">100%</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-xl py-16 px-6 text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                  <Truck className="w-5 h-5 text-slate-400" />
                </div>
                <p className="text-sm text-slate-700 font-medium mb-1">
                  {selectedVehicle
                    ? `No miles on file for truck ${selectedVehicle} in ${presetYear}`
                    : `No IFTA mileage imported for ${presetYear} yet`}
                </p>
                <p className="text-xs text-slate-500 mb-4">
                  Upload the ProLogs Vehicle mileage report CSV to populate the quarterly breakdown.
                </p>
                <button
                  onClick={handleImportFilePick}
                  disabled={importing}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Import CSV
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {importPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-emerald-600" />
                <div>
                  <div className="text-base font-semibold text-slate-900">Import ProLogs mileage CSV</div>
                  {importFileNames.length > 0 && (
                    <div className="text-xs text-slate-500 font-mono mt-0.5 break-all">
                      {importFileNames.length === 1
                        ? importFileNames[0]
                        : `${importFileNames.length} files: ${importFileNames.join(', ')}`}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={handleCancelImport}
                disabled={importing}
                className="p-1 rounded-lg hover:bg-slate-100 disabled:opacity-50"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="px-6 py-4 overflow-y-auto">
              <div className="mb-4">
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">
                  Name this import <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={importLabel}
                  onChange={(e) => setImportLabel(e.target.value)}
                  placeholder="e.g. Acme Trucking — Q1 2026"
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Shown on the mileage card. Re-uploading with the same name replaces the prior import; different names stay side-by-side.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="text-xs text-slate-500 uppercase tracking-wide">Trip rows</div>
                  <div className="text-lg font-semibold text-slate-900 tabular-nums">
                    {importPreview.totalRows.toLocaleString()}
                  </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="text-xs text-slate-500 uppercase tracking-wide">Total miles</div>
                  <div className="text-lg font-semibold text-slate-900 tabular-nums">
                    {Math.round(importPreview.totalMiles).toLocaleString()}
                  </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="text-xs text-slate-500 uppercase tracking-wide">Date range</div>
                  <div className="text-sm font-semibold text-slate-900 tabular-nums">
                    {importPreview.dateRange
                      ? `${importPreview.dateRange.min} → ${importPreview.dateRange.max}`
                      : '—'}
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800 mb-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Matched — {importPreview.matched.length} vehicle{importPreview.matched.length === 1 ? '' : 's'}
                </div>
                {importPreview.matched.length === 0 ? (
                  <div className="text-sm text-slate-500 italic">No vehicles in the CSV matched your TMS trucks — all rows will still be imported (unlinked).</div>
                ) : (
                  <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="text-left px-3 py-2">CSV ID</th>
                          <th className="text-left px-3 py-2">Matched unit</th>
                          <th className="text-right px-3 py-2">Rows</th>
                          <th className="text-right px-3 py-2">Miles</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.matched.map(m => (
                          <tr key={m.vehicleId} className="border-t border-slate-100">
                            <td className="px-3 py-1.5 font-mono">{m.vehicleId}</td>
                            <td className="px-3 py-1.5 font-mono">{m.unitNumber}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{m.rowCount.toLocaleString()}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{Math.round(m.miles).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {importPreview.unmatched.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-amber-800 mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    Unmatched — {importPreview.unmatched.length} vehicle{importPreview.unmatched.length === 1 ? '' : 's'} (imported without a truck link)
                  </div>
                  <div className="max-h-32 overflow-y-auto border border-amber-200 bg-amber-50 rounded-lg p-3">
                    <div className="flex flex-wrap gap-1.5">
                      {importPreview.unmatched.map(u => (
                        <span key={u.vehicleId} className="inline-flex items-center px-2 py-0.5 rounded bg-white border border-amber-300 text-xs font-mono text-amber-900">
                          {u.vehicleId} ({Math.round(u.miles).toLocaleString()} mi)
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {importDuplicateRows > 0 && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                  <strong>Overlapping files:</strong> {importDuplicateRows.toLocaleString()} trip row{importDuplicateRows === 1 ? ' was' : 's were'} identical
                  to a row in another selected file and {importDuplicateRows === 1 ? 'was' : 'were'} counted once.
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
                <strong>Heads up:</strong> If an import named "{importLabel.trim() || '…'}" already exists, it is deleted and replaced by this one.
                Imports with other names are kept, so don't load the same trucks and dates twice under different names — the miles would be counted twice.
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                onClick={handleCancelImport}
                disabled={importing}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmImport}
                disabled={importing || importPreview.totalRows === 0 || !importLabel.trim()}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {importing ? 'Importing…' : 'Confirm import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showHelp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-bold text-slate-900">How the IFTA workflow works</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  End-to-end quarterly filing process for your fleet.
                </p>
              </div>
              <button
                onClick={() => setShowHelp(false)}
                className="p-2 rounded-lg hover:bg-slate-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 py-5 flex-1 overflow-y-auto text-sm text-slate-700 space-y-5">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Filing deadlines</div>
                <p>Q1 → Apr 30 · Q2 → Jul 31 · Q3 → Oct 31 · Q4 → Jan 31. File with your base jurisdiction (the state where your IFTA license is registered).</p>
              </div>

              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">During the quarter</div>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Drivers run ProLogs ELD — miles and jurisdictions accumulate inside ProLogs</li>
                  <li>Drivers fuel up — keep receipts and/or rely on the fuel-card statement you'll import later</li>
                </ul>
                <p className="text-xs text-slate-500 mt-1">Nothing flows into TMS automatically. All three data sources (miles, fuel, rates) are pulled in manually after the quarter ends.</p>
              </div>

              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">After the quarter ends — 5 steps</div>

                <div className="space-y-3">
                  <div className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                    <div className="font-semibold text-slate-900">1. Load tax rates</div>
                    <div className="text-xs text-slate-600 mt-1">
                      <span className="font-medium">IFTA → Tax Rates tab.</span> Pick the quarter, click the green <em>Fetch {`{Q}`} rates</em> button. Confirms all 58 jurisdictions have rates.
                    </div>
                  </div>

                  <div className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                    <div className="font-semibold text-slate-900">2. Import fuel purchases</div>
                    <div className="text-xs text-slate-600 mt-1">
                      <span className="font-medium">Accounting → Fuel tab.</span> Upload the quarterly statement CSV from <em>every</em> fuel card / vendor your drivers use (Pilot, Comdata, WEX, EFS, TCH, etc.). Missing one vendor = missing fuel = Fleet MPG goes crazy high on the return. Verify gallons + state are populated on each row.
                    </div>
                  </div>

                  <div className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                    <div className="font-semibold text-slate-900">3. Import state miles from ProLogs</div>
                    <div className="text-xs text-slate-600 mt-1">
                      <span className="font-medium">IFTA → Mileage tab.</span> In ProLogs, export the <em>Vehicle mileage report</em> CSV for the quarter. Click <em>Import CSV</em> and select every file at once (one file with many trucks, or one file per truck). Give it a name (e.g. "2026-Q3") and confirm — each import lives as its own card. Re-using a name replaces that import; never load the same trucks and dates under two names.
                    </div>
                  </div>

                  <div className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                    <div className="font-semibold text-slate-900">4. Generate the return</div>
                    <div className="text-xs text-slate-600 mt-1">
                      <span className="font-medium">IFTA → Return tab.</span> Pick the quarter and review 4 red flags:
                      <ul className="list-disc pl-5 mt-1 space-y-0.5">
                        <li><span className="font-medium">Fleet MPG</span> should be <span className="font-medium">5–8</span> (if 20+, fuel purchases are missing)</li>
                        <li><span className="font-medium">Total Tax Due</span> should be reasonable ($1k–$10k for ~20 trucks)</li>
                        <li>Jurisdictions with miles but <span className="font-medium">0 fuel</span> → receipts missing for that state</li>
                        <li>Large negative tax rows → driver bought way more fuel than burned there (rare, investigate)</li>
                      </ul>
                    </div>
                  </div>

                  <div className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                    <div className="font-semibold text-slate-900">5. Export and file</div>
                    <div className="text-xs text-slate-600 mt-1">
                      Click <span className="font-medium">CSV</span> on the Return tab. Log into your base jurisdiction's IFTA portal. Enter per-state miles + gallons from the CSV. Pay (or request refund) by the deadline.
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Pre-submit checklist</div>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Fleet MPG lands 5–8</li>
                  <li>Every state with miles has fuel receipts (or you're certain no driver fueled there)</li>
                  <li>Every truck you file for has a matching import card (no silent drops)</li>
                  <li>Quarter matches (correct Q + year)</li>
                  <li>Base jurisdiction's portal total matches your CSV total</li>
                </ul>
              </div>
            </div>

            <div className="px-6 py-3 border-t border-slate-200 flex justify-end">
              <button
                onClick={() => setShowHelp(false)}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
